import { FC, useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';

import { useGuardRecords } from '@/hooks/api/useGuardRecords';
import { NetworkSettings, useDucatClient } from '@/context/DucatClientCtx';
import { getConfig } from '@/config/config';
import { NetworkEnum } from '@/config/config.types';
import Drawer from '@/components/drawer';
import { ALLOW_GUARDIAN_RANDOMIZE } from '@/constants';

import NetworkDropdown from './network-dropdown';
import NetworkInput from './network-input';
import { getOptions, getRandomItem, isMissingRequiredFields } from './utils';
import { networkOptions } from './data';

interface NetworkSettingsProps {
  isOpen: boolean;
  openNetworkSettings: () => void;
  closeNetworkSettings: () => void;
}

const NetworkSettingsDrawer: FC<NetworkSettingsProps> = ({ isOpen, openNetworkSettings, closeNetworkSettings }) => {
  const {
    contract,
    networkSettings,
    selectedNetwork,
    setNetworkSettings,
    handleNetworkChange: handleNetworkContextChange,
  } = useDucatClient();
  const networkSettingsConfig = getConfig(selectedNetwork as NetworkEnum);

  const initialSettings = {
    network: selectedNetwork,
    masterId: networkSettingsConfig.masterContractId,
    guardianUrl: networkSettingsConfig.guardians[0].guardianUrl,
    guardianPublicKey: networkSettingsConfig.guardianPubkey,
    customGuardian: false,
  };

  const [settings, setSettings] = useState<NetworkSettings>(initialSettings);
  const [isManualMasterIdChange, setIsManualMasterIdChange] = useState<boolean>(false);
  const [currentDefaultMasterId, setCurrentDefaultMasterId] = useState<string>(networkSettingsConfig.masterContractId);
  const { data: guards } = useGuardRecords({ network: settings.network, contract }, { enabled: !!contract });

  useEffect(() => {
    if (networkSettings) {
      setSettings({
        network: networkSettings.network,
        masterId: networkSettings.masterId,
        guardianUrl: networkSettings.guardianUrl,
        guardianPublicKey: networkSettings.guardianPublicKey,
        customGuardian: networkSettings.customGuardian,
      });
    }
  }, [networkSettings]);

  const guardianOptions = useMemo(() => {
    const options = getOptions(guards, settings);

    return [...options, { label: 'Custom', value: 'custom' }];
  }, [guards]); // TODO: check settings is missed in deps

  const handleNetworkChange = (option: { label: string; value: string }) => {
    const networkConfig = getConfig(option.value as NetworkEnum);
    setCurrentDefaultMasterId(networkConfig.masterContractId);

    setSettings({
      network: option.value as NetworkEnum,
      masterId: networkConfig.masterContractId,
      guardianUrl: ALLOW_GUARDIAN_RANDOMIZE ? getRandomItem(networkConfig.guardians).guardianUrl : networkConfig.guardians[0]?.guardianUrl,
      guardianPublicKey: networkConfig.guardianPubkey,
      customGuardian: false,
    });

    setIsManualMasterIdChange(false);
  };

  const handleGuardianChange = (option: { label: string; value: string }) => {
    if (option.value === 'custom') {
      setSettings({
        ...settings,
        customGuardian: true,
        guardianUrl: '',
        guardianPublicKey: '',
      });
    } else {
      const selectedGuard = guards?.find((g) => g.url === option.value);
      setSettings({
        ...settings,
        customGuardian: false,
        guardianUrl: option.value,
        guardianPublicKey: selectedGuard?.pub || '',
      });
    }
  };

  const handleMasterIdChange = (value: string) => {
    setSettings({ ...settings, masterId: value });
    setIsManualMasterIdChange(true);
  };

  // We want to save on close, but reset on "x" and on "Cancel". FIXME: async/await looks redundant.
  const handleSave = async () => {
    try {
      if (isMissingRequiredFields(settings)) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (settings.network !== selectedNetwork) {
        await handleNetworkContextChange(settings.network);
        toast('Network settings updated successfully');
      } else if (settings.guardianUrl !== networkSettings.guardianUrl) {
        toast('Network settings updated successfully');
      }

      setNetworkSettings(settings);
      closeNetworkSettings();
    } catch (error) {
      toast('Failed to update network settings');
      console.error('Error updating network settings:', error);
    }
  };

  function onOpenChange(isOpen: boolean) {
    if (!isOpen) {
      handleSave();
    } else {
      openNetworkSettings();
    }
  }

  function onCancel() {
    closeNetworkSettings();
    setSettings(initialSettings);
  }

  const handleResetMasterId = () => {
    setSettings({
      ...settings,
      masterId: currentDefaultMasterId,
    });
    setIsManualMasterIdChange(false);
  };

  return (
    <Drawer
      isOpen={isOpen}
      title='Network Settings'
      totalSteps={1}
      backButtonText='Cancel'
      nextButtonText='Save'
      currentStep={1}
      isNextDisabled={false}
      onOpenChange={onOpenChange}
      onCancel={onCancel}
      onBack={onCancel}
      onNext={handleSave}
    >
      <div className='flex flex-col gap-8 w-full'>
        <h1 className='text-text-secondary leading-6 text-xl tracking-02em'>Configure your network settings and guardian preferences</h1>
        <NetworkDropdown
          label='Select Network'
          options={networkOptions}
          value={{ label: settings.network, value: settings.network }}
          onChange={handleNetworkChange}
          disabled={true}
        />
        <NetworkInput
          label='Master ID'
          value={settings.masterId}
          defaultValue={currentDefaultMasterId}
          onChange={handleMasterIdChange}
          onReset={handleResetMasterId}
          showReset={isManualMasterIdChange}
        />
        <NetworkDropdown
          label='Select Guardian'
          options={guardianOptions}
          value={{
            label: settings.customGuardian
              ? 'Custom'
              : guardianOptions.find((v) => v.value === settings.guardianUrl)?.label || settings.guardianUrl,
            value: settings.customGuardian ? 'custom' : settings.guardianUrl,
          }}
          onChange={handleGuardianChange}
          noCapitalize={true}
        />
        {settings.customGuardian ? (
          <>
            <NetworkInput
              label='Guardian URL'
              value={settings.guardianUrl}
              placeholder='Enter guardian url...'
              onChange={(value) => setSettings({ ...settings, guardianUrl: value })}
            />
            <NetworkInput
              label='Guardian Public Key'
              value={settings.guardianPublicKey}
              placeholder='Enter public key...'
              onChange={(value) => setSettings({ ...settings, guardianPublicKey: value })}
            />
          </>
        ) : (
          <div className='flex flex-col gap-1'>
            <div className='text-text-secondary text-xl'>Group Public Key</div>
            <div className='text-text-primary break-words'>{settings.guardianPublicKey}</div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default NetworkSettingsDrawer;
